import React, { useEffect } from 'react';
import styled from 'styled-components';
import { lighten } from 'polished';
import { TPortfolioPageProps } from 'pages/portfolio';
import { portfolioPage, portfolioPlusIcon } from 'pages/portfolio/test-ids';
import BasicLayout from 'components/layout/basic-layout';
import { ActionBar, Container, ToggleButtonContainer, ShareButton } from 'pages/compare/components';
import ToggleButton from 'components/buttons/toggle';
import UnitToggle from 'components/unit-toggle';
import CostFormulaToggle from 'components/cost-formula-toggle';
import { EForecastEvaluationType } from 'store/deal-evaluation/types';
import ParetoAddDealModal from 'pages/pareto/add-deal-modal';
import { Control, ControlGroup, Controls } from 'components/visuals/_common';
import colors from 'theme/colors';
import { IDeal } from 'store/deals/types';
import { toast } from 'react-toastify';
import PlusIcon from '@material-ui/icons/ControlPoint';
import PortfolioDefaultView from 'pages/portfolio/default-view';
import { EModalTitle } from 'lib/storage';
import UrlShare from 'components/url-share';
import ModalBasic from 'components/modal-provider/modal-basic';
import { ModalButton } from 'components/modal-provider/modal';
import labels from 'constants/labels';
import messages from 'constants/messages';
import MyBookmark from 'pages/bookmark/bookmark-common';

const ActionBarRight = styled.div`
  display: flex;
  align-items: center;
`;

const PortfolioContainer = styled.div`
  padding: 8px 32px;
`;

const PortfolioControlGroup = styled(ControlGroup)`
  justify-content: flex-start;
`;

const lightenValue = 0.2;

const ControlAddDeal = styled(Control)`
  display: flex;
  align-items: center;
  margin-left: auto;

  svg {
    color: ${colors.primary};
    transition: color 0.2s ease;
    margin-right: 8px;
    font-size: 3em;

    &:hover {
      cursor: pointer;
      color: ${lighten(lightenValue, colors.primary)};
    }
  }

  span {
    font-size: 10px;
  }
`;

const PortfolioPage = ({
  evaluateDeals,
  selectedForecastType,
  setForecastType,
  setParetoIsModalOpen,
  portfolioDealIds,
  setPortfolioDealIds,
  clearModalContent,
  setModalContent,
  filteredDeals,
}: TPortfolioPageProps) => {
  useEffect(() => {
    const urlText: string = window.location.href;
    if (urlText.lastIndexOf('/portfolio/') !== -1) {
      const urlDealIds: string[] = urlText
        .substring(urlText.lastIndexOf('/') + 1)
        .toString()
        .split(',');
      const dealIds: string[] = [];
      filteredDeals.forEach((deal: IDeal) => {
        if (deal.id && urlDealIds.includes(deal.id)) {
          dealIds.push(deal.id);
        }
      });
      if (dealIds.length === 0) {
        toast.warn(messages.warnings.dealNotFound, { autoClose: 5000 });
        return;
      }
      setPortfolioDealIds(dealIds);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIhsForecastClick = () => {
    if (selectedForecastType === EForecastEvaluationType.IHS) {
      return;
    }
    setForecastType(EForecastEvaluationType.IHS);
    evaluateDeals();
  };

  const handleShellForecastClick = () => {
    if (selectedForecastType === EForecastEvaluationType.SHELL) {
      return;
    }
    setForecastType(EForecastEvaluationType.SHELL);
    evaluateDeals();
  };

  function handleShare() {
    const modalContent = (
      <>
        <p>{messages.info.shareDeal}</p>
        <UrlShare url={window.location.href} />
        <br />
      </>
    );
    const modalButton = <ModalButton onClick={clearModalContent}>{labels.button.close}</ModalButton>;
    const modal = (
      <ModalBasic
        title={labels.titles.modals.shareDeal}
        content={modalContent}
        onClose={clearModalContent}
        buttons={modalButton}
      />
    );
    setModalContent(modal);
  }

  return (
    <BasicLayout data-testid={portfolioPage}>
      <Container>
        <ActionBar>
          <ToggleButtonContainer>
            <ToggleButton
              isPrimaryOptionActive={selectedForecastType === EForecastEvaluationType.IHS}
              primaryOptionText="IHS Forecast"
              secondaryOptionText="Shell Forecast"
              onPrimaryOptionClick={handleIhsForecastClick}
              onSecondaryOptionClick={handleShellForecastClick}
            />
            <UnitToggle showTitle={false} />
            <CostFormulaToggle showTitle={false} />
          </ToggleButtonContainer>
          <ActionBarRight>
            <MyBookmark bookmarkModule="Portfolio" bookmarkLink={window.location.pathname} />
            <ShareButton disabled={portfolioDealIds.length === 0} onClick={handleShare}>
              Share
            </ShareButton>
          </ActionBarRight>
        </ActionBar>
        <PortfolioContainer>
          <Controls>
            <PortfolioControlGroup>
              <ControlAddDeal>
                <PlusIcon
                  data-testid={portfolioPlusIcon}
                  type="button"
                  onClick={() => setParetoIsModalOpen(true, EModalTitle.PORTFOLIO)}
                />
                <span>Click here to add deal</span>
              </ControlAddDeal>
            </PortfolioControlGroup>
          </Controls>
          <PortfolioDefaultView />
          <ParetoAddDealModal />
        </PortfolioContainer>
      </Container>
    </BasicLayout>
  );
};

export default PortfolioPage;
